import { useState, type FormEvent } from 'react';
import { adminApi } from '@/lib/admin';
import { supabase } from '@/lib/supabase';
import { SectionCard, ConfirmDialog } from '@/pages/admin/components/UI';

export default function AdminSettings() {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!oldPassword || !newPassword || !confirmPassword) {
      setError('请填写完整信息');
      return;
    }
    if (newPassword.length < 8) {
      setError('新密码至少需要 8 位');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('两次输入的新密码不一致');
      return;
    }
    if (newPassword === oldPassword) {
      setError('新密码不能与当前密码相同');
      return;
    }
    setSubmitting(true);
    try {
      await adminApi.changeAdminPassword(oldPassword, newPassword);
      setSuccess('密码修改成功，请牢记新密码');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : '修改失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const [clearOpen, setClearOpen] = useState(false);
  const [clearInput, setClearInput] = useState('');
  const [clearLoading, setClearLoading] = useState(false);
  const [clearResult, setClearResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const handleClearAll = async () => {
    if (clearInput.trim() !== '清空') {
      setClearResult({ ok: false, msg: '请输入「清空」二字以确认' });
      return;
    }
    setClearLoading(true);
    setClearResult(null);
    try {
      const res = await adminApi.clearAllData();
      const kept = res?.kept_admin_profiles ?? 0;
      const deletedAuth = res?.deleted_auth_users ?? 0;
      setClearResult({
        ok: true,
        msg: `数据已清空：保留 ${kept} 个管理员档案，删除 ${deletedAuth} 个登录账号，所有业务数据已清除。`,
      });
      setClearOpen(false);
      setClearInput('');
    } catch (err) {
      setClearResult({
        ok: false,
        msg: err instanceof Error ? err.message : '清空失败，请重试',
      });
    } finally {
      setClearLoading(false);
    }
  };

  const handleTestEmail = async () => {
    setTestLoading(true);
    setTestResult(null);
    try {
      const res = await supabase.functions.invoke('send-fund-email', {
        body: {
          type: 'deposit',
          title: '测试邮件',
          amount: '¥0.00',
          user: '系统测试',
          items: [{ label: '说明', value: '这是一封测试邮件' }],
          time: new Date().toLocaleString('zh-CN'),
        },
      });
      if (res.error) {
        setTestResult({ ok: false, msg: `调用失败：${res.error.message}` });
      } else if (res.data?.success) {
        setTestResult({ ok: true, msg: '测试邮件已成功发送，请查收邮箱（含垃圾箱）' });
      } else {
        setTestResult({ ok: false, msg: res.data?.error || '发送失败，未知错误' });
      }
    } catch (err) {
      setTestResult({ ok: false, msg: err instanceof Error ? err.message : '测试失败' });
    } finally {
      setTestLoading(false);
    }
  };

  const inputCls =
    'w-full px-4 py-2.5 rounded-lg border border-brown-200 text-sm text-brown-900 placeholder:text-brown-300 outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-500/10 transition-all';

  return (
    <div className="max-w-xl space-y-5">
      <SectionCard title="修改登录密码">
        <form onSubmit={handleSubmit} className="p-4 md:p-5 space-y-4" noValidate>
          {/* 当前密码 */}
          <div>
            <label className="block text-brown-600 text-xs font-medium mb-1.5">当前密码</label>
            <div className="relative">
              <input
                type={showOld ? 'text' : 'password'}
                value={oldPassword}
                onChange={(e) => {
                  setOldPassword(e.target.value);
                  if (error) setError('');
                  if (success) setSuccess('');
                }}
                autoComplete="current-password"
                placeholder="请输入当前登录密码"
                className={`${inputCls} pr-11`}
              />
              <button
                type="button"
                onClick={() => setShowOld(!showOld)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-brown-400 hover:text-brown-600 cursor-pointer"
                tabIndex={-1}
              >
                <i className={`${showOld ? 'ri-eye-line' : 'ri-eye-off-line'} text-lg`}></i>
              </button>
            </div>
          </div>

          {/* 新密码 */}
          <div>
            <label className="block text-brown-600 text-xs font-medium mb-1.5">新密码</label>
            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (error) setError('');
                  if (success) setSuccess('');
                }}
                autoComplete="new-password"
                placeholder="至少 8 位，建议包含字母和数字"
                className={`${inputCls} pr-11`}
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-brown-400 hover:text-brown-600 cursor-pointer"
                tabIndex={-1}
              >
                <i className={`${showNew ? 'ri-eye-line' : 'ri-eye-off-line'} text-lg`}></i>
              </button>
            </div>
          </div>

          {/* 确认新密码 */}
          <div>
            <label className="block text-brown-600 text-xs font-medium mb-1.5">确认新密码</label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (error) setError('');
                  if (success) setSuccess('');
                }}
                autoComplete="new-password"
                placeholder="再次输入新密码"
                className={`${inputCls} pr-11`}
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-brown-400 hover:text-brown-600 cursor-pointer"
                tabIndex={-1}
              >
                <i className={`${showConfirm ? 'ri-eye-line' : 'ri-eye-off-line'} text-lg`}></i>
              </button>
            </div>
          </div>

          {/* 错误提示 */}
          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5">
              <span className="w-4 h-4 flex items-center justify-center text-red-500 flex-shrink-0">
                <i className="ri-error-warning-line text-sm"></i>
              </span>
              <span className="text-xs text-red-600">{error}</span>
            </div>
          )}

          {/* 成功提示 */}
          {success && (
            <div className="flex items-center gap-2 bg-green-50 border border-green-100 rounded-lg px-3 py-2.5">
              <span className="w-4 h-4 flex items-center justify-center text-green-600 flex-shrink-0">
                <i className="ri-checkbox-circle-line text-sm"></i>
              </span>
              <span className="text-xs text-green-700">{success}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:w-auto px-8 py-2.5 rounded-lg bg-gold-500 hover:bg-gold-600 text-brown-950 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            {submitting ? '保存中...' : '确认修改'}
          </button>
        </form>
      </SectionCard>

      <SectionCard title="邮件通知测试">
        <div className="p-4 md:p-5 space-y-4">
          <p className="text-xs text-brown-500 leading-relaxed">
            如果下单 / 结算 / 出入金的邮件收不到，点下面按钮发一封测试邮件，系统会把 Resend
            返回的具体错误显示出来，方便定位问题。
          </p>
          <button
            type="button"
            onClick={handleTestEmail}
            disabled={testLoading}
            className="w-full sm:w-auto px-8 py-2.5 rounded-lg bg-gold-500 hover:bg-gold-600 text-brown-950 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            {testLoading ? '发送中...' : '发送测试邮件'}
          </button>
          {testResult && (
            <div
              className={`flex items-start gap-2 rounded-lg px-3 py-2.5 ${
                testResult.ok ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'
              }`}
            >
              <span
                className={`w-4 h-4 flex items-center justify-center flex-shrink-0 ${
                  testResult.ok ? 'text-green-600' : 'text-red-500'
                }`}
              >
                <i className={`${testResult.ok ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} text-sm`}></i>
              </span>
              <span className={`text-xs break-all ${testResult.ok ? 'text-green-700' : 'text-red-600'}`}>
                {testResult.msg}
              </span>
            </div>
          )}
        </div>
      </SectionCard>

      <SectionCard title="危险操作">
        <div className="p-4 md:p-5 space-y-4">
          <div className="bg-red-50 border border-red-100 rounded-lg px-4 py-3">
            <p className="text-xs text-red-600 leading-relaxed">
              <strong>清空所有数据</strong>：删除所有普通用户账号、订单、出入金、提货、持仓、消息、地址、银行卡、支付等业务数据。
              会保留管理员账号、收款通道、公告、交易时间表和行情价格。此操作<strong>不可恢复</strong>，请谨慎。
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setClearOpen(true);
              setClearInput('');
              setClearResult(null);
            }}
            className="px-8 py-2.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            清空所有数据
          </button>
        </div>
      </SectionCard>

      <ConfirmDialog
        open={clearOpen}
        title="确认清空所有数据"
        desc="此操作不可恢复，请输入「清空」二字以确认。"
        confirmText={clearLoading ? '清空中...' : '确认清空'}
        danger
        loading={clearLoading}
        onCancel={() => setClearOpen(false)}
        onConfirm={handleClearAll}
      >
        <input
          type="text"
          value={clearInput}
          onChange={(e) => setClearInput(e.target.value)}
          placeholder="请输入：清空"
          className="w-full px-4 py-2.5 rounded-lg border border-red-200 text-sm text-brown-900 placeholder:text-brown-300 outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/10 transition-all"
        />
        {clearResult && (
          <div
            className={`mt-3 flex items-start gap-2 rounded-lg px-3 py-2.5 ${
              clearResult.ok ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'
            }`}
          >
            <span
              className={`w-4 h-4 flex items-center justify-center flex-shrink-0 ${
                clearResult.ok ? 'text-green-600' : 'text-red-500'
              }`}
            >
              <i className={`${clearResult.ok ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} text-sm`}></i>
            </span>
            <span className={`text-xs break-all ${clearResult.ok ? 'text-green-700' : 'text-red-600'}`}>
              {clearResult.msg}
            </span>
          </div>
        )}
      </ConfirmDialog>

      <p className="text-brown-400 text-xs">修改后请牢记新密码，下次登录将以新密码为准。</p>
    </div>
  );
}